import * as React from "react";
import Tooltip from "@Elements/Tooltip";
import * as Icons from "@Icons/FontAwesome"
import { ISensorDTO } from "./types";
import { DeviceType, DeviceTypeLabel, SensorTypeEnum } from "./SensorTypeEnum";
import AlarmWristband from "@Icons/Safemate/solid/AlarmWristband";
import UniversalSensorImage from "../../../../assets/images/sensors/UT-15SL-01.png";
import DoorContactImage from "../../../../assets/images/sensors/DC-23-R3-02.png";
import FallSensorImage from "../../../../assets/images/sensors/fall_sensor_001.png";
import PirSensorImage from "../../../../assets/images/sensors/IR-29.png";
import PanicButtonImage from "../../../../assets/images/sensors/PB-23.png";
import PullCordImage from "../../../../assets/images/sensors/PCU-3_01.png";
import SmokeSensorImage from "../../../../assets/images/sensors/SD-29.png";
import TemperatureSensorImage from "../../../../assets/images/sensors/TS-9.png";
import DectSensorImage from "../../../../assets/images/sensors/VCP-DECT-R8-03.png";
import PirCameraSensorImage from "../../../../assets/images/sensors/VST-892HD.png";
import WadcSensorImage from "../../../../assets/images/sensors/WADC-1.png";
import WaterSensorImage from "../../../../assets/images/sensors/WLS-15-01.png";
import PendantImage from "../../../../assets/images/sensors/WTRZ-01.png";
import Deha32 from "@Elements/Icon/Icons/DeviceIcons/Deha32";

export const mapType = (sensor: ISensorDTO) => {
  switch(sensor.sensor.type){
    case SensorTypeEnum.WRIST_BAND:
    case SensorTypeEnum.BASE_STATION:
      return getIconForType(sensor.sensor.type);

    case SensorTypeEnum.SAFEMATE_HOME:
      return getIconForType(sensor.type.code)


    default: return null;
  }
}

export interface ITypeLabel {
  popoverId: string;
  labelId: string;
  defaultMessage: string;
}

/* Single source of truth for a device type's human-readable, translatable label.
   Shared by the type icon tooltip and any other place that needs to name a type. */
export const getTypeLabel = (type: DeviceType | string): ITypeLabel => {
  switch (type) {
    case DeviceType.DOOR_CONTACT:
    case DeviceType.WADC_1:
      return { popoverId: "doorContactPopover", labelId: DeviceTypeLabel.DOOR_CONTACT_LABEL, defaultMessage: "Dørsensor" };
    case DeviceType.WATER_DEVICE:
      return { popoverId: "waterDevicePopover", labelId: DeviceTypeLabel.WATER_DEVICE_LABEL, defaultMessage: "Vannsensor" };
    case DeviceType.PANIC_BUTTON:
      return { popoverId: "panicButtonPopover", labelId: DeviceTypeLabel.PANIC_BUTTON_LABEL, defaultMessage: "Alarmknapp" };
    case DeviceType.PIR_SENSOR:
      return { popoverId: "pirSensorPopover", labelId: DeviceTypeLabel.PIR_SENSOR_LABEL, defaultMessage: "PIR-sensor" };
    case DeviceType.PIR_SENSOR_CAMERA:
      return { popoverId: "priSensorCameraPopover", labelId: DeviceTypeLabel.PIR_SENSOR_CAMERA_LABEL, defaultMessage: "Camera PIR-sensor" };
    case '11':
    case DeviceType.SMOKE_DETECTOR:
      return { popoverId: "smokeDetectorPopover", labelId: DeviceTypeLabel.SMOKE_DETECTOR_LABEL, defaultMessage: "Røyksensor" };
    case DeviceType.THERMOSTAT:
      return { popoverId: "thermostatPopover", labelId: DeviceTypeLabel.THERMOSTAT_LABEL, defaultMessage: "Termostat" };
    case DeviceType.PENDANT:
      return { popoverId: "pendantPopover", labelId: DeviceTypeLabel.PENDANT_LABEL, defaultMessage: "Alarmsmykke" };
    case DeviceType.PULL_CORD:
      return { popoverId: "pullCordPopover", labelId: DeviceTypeLabel.PULL_CORD_LABEL, defaultMessage: "Dra-snor enhet" };
    case DeviceType.UNIVERSAL_SENSOR:
    case DeviceType.UNIVERSAL_SENSOR_SPT_1:
      return { popoverId: "universalSensorPopover", labelId: DeviceTypeLabel.UNIVERSAL_SENSOR_LABEL, defaultMessage: "Universalsensor" };
    case DeviceType.DECT_MOBILE:
      return { popoverId: "mobileDectPopover", labelId: DeviceTypeLabel.DECT_MOBILE_LABEL, defaultMessage: "Mobil DECT-enhet" };
    case DeviceType.FALL_ALARM:
      return { popoverId: "fallAlarmPopover", labelId: DeviceTypeLabel.FALL_ALARM_LABEL, defaultMessage: "Fallalarm" };
    case DeviceType.DECT_MOUNTED:
      return { popoverId: "stationaryDectPopover", labelId: DeviceTypeLabel.DECT_MOUNTED_LABEL, defaultMessage: "Stasjonær DECT-enhet" };
    case DeviceType.LIGHT_BULB:
      return { popoverId: "lightBulbPopover", labelId: DeviceTypeLabel.LIGHT_BULB_LABEL, defaultMessage: "Lyspæresensor" };
    case DeviceType.VCP:
      return { popoverId: "lightBulbPopover", labelId: DeviceTypeLabel.VCP_LABEL, defaultMessage: "Vcp enhet" };
    case DeviceType.WRIST_BAND:
      return { popoverId: "wristbandPopover", labelId: DeviceTypeLabel.WRIST_BAND_LABEL, defaultMessage: "Armbånd" };
    case DeviceType.BASE_STATION:
      return { popoverId: "baseStationPopover", labelId: DeviceTypeLabel.BASE_STATION_LABEL, defaultMessage: "Basestasjon" };
    case '0':
      return { popoverId: "unknownPopover", labelId: DeviceTypeLabel.UNKNOWN_LABEL, defaultMessage: "Ukjent" };
    default:
      return { popoverId: "unsupportedPopover", labelId: DeviceTypeLabel.UNSUPPORTED_LABEL, defaultMessage: "Støttes ikke" };
  }
};

/* The bare icon component for a device type (no tooltip wrapper). */
const getTypeIconElement = (type: DeviceType | string) => {
  switch (type) {
    case DeviceType.DOOR_CONTACT:
      return <Icons.DoorContact mediumSecondary />;
    case DeviceType.WADC_1:
      return <Icons.Wadc1 mediumSecondary />;
    case DeviceType.WATER_DEVICE:
      return <Icons.WaterDevice mediumSecondary />;
    case DeviceType.PANIC_BUTTON:
      return <Icons.PanicButton mediumSecondary />;
    case DeviceType.PIR_SENSOR:
      return <Icons.PIR mediumSecondary />;
    case DeviceType.PIR_SENSOR_CAMERA:
      return <Icons.PIRCamera mediumSecondary />;
    case '11':
    case DeviceType.SMOKE_DETECTOR:
      return <Icons.SmokeDetector mediumSecondary />;
    case DeviceType.THERMOSTAT:
      return <Icons.Thermostat mediumSecondary />;
    case DeviceType.PENDANT:
      return <Icons.Pendant mediumSecondary />;
    case DeviceType.PULL_CORD:
      return <Icons.PullCord mediumSecondary />;
    case DeviceType.UNIVERSAL_SENSOR:
    case DeviceType.UNIVERSAL_SENSOR_SPT_1:
      return <Icons.UniversalSensor mediumSecondary />;
    case DeviceType.DECT_MOBILE:
      return <Icons.DectMobile mediumSecondary />;
    case DeviceType.FALL_ALARM:
      return <Icons.FallSensor mediumSecondary />;
    case DeviceType.DECT_MOUNTED:
      return <Icons.DectMounted mediumSecondary />;
    case DeviceType.LIGHT_BULB:
      return <Icons.LightBulb mediumSecondary />;
    case DeviceType.VCP:
      return <Deha32 size="large" />;
    case DeviceType.WRIST_BAND:
      return <AlarmWristband size="large" />;
    case DeviceType.BASE_STATION:
      return <Icons.ChargingStation mediumSecondary />;
    case '0':
      return <Icons.Unknown mediumSecondary />;
    default:
      return <Icons.Triangle mediumSecondary />;
  }
};

export const getIconForType = (type: DeviceType | string) => {
  const { popoverId, labelId, defaultMessage } = getTypeLabel(type);
  return (
    <Tooltip popoverId={popoverId} labelId={labelId} defaultMessage={defaultMessage}>
      {getTypeIconElement(type)}
    </Tooltip>
  );
};

export const getImageForType = (type: DeviceType | string) => {
  switch (type) {
    case DeviceType.DOOR_CONTACT:
      return (
        <img src={DoorContactImage}/>
      );
    case DeviceType.WADC_1:
      return (
        <img src={WadcSensorImage}/>
      );
    case DeviceType.WATER_DEVICE:
      return (
        
        <img src={WaterSensorImage}/>
      );
    case DeviceType.PANIC_BUTTON:
      return (
        <img src={PanicButtonImage}/>
      );
    case DeviceType.PIR_SENSOR:
      return (
        <img src={PirSensorImage}/>
      );
    case DeviceType.PIR_SENSOR_CAMERA:
      return(
        <img src={PirCameraSensorImage}/>
      );
    case '11':
    case DeviceType.SMOKE_DETECTOR:
      return (
        <img src={SmokeSensorImage}/>
      );
    case DeviceType.THERMOSTAT:
      return (
        <img src={TemperatureSensorImage}/>
      );
    case DeviceType.PENDANT:
      return (
        <img src={PendantImage}/>
      );
    case DeviceType.PULL_CORD:
      return (
        <img src={PullCordImage}/>
      );
    case DeviceType.UNIVERSAL_SENSOR:
    case DeviceType.UNIVERSAL_SENSOR_SPT_1:
      return (
        <img src={UniversalSensorImage}/>
      );
    case DeviceType.DECT_MOBILE:
      return (
        <img src={DectSensorImage}/>
      );
    case DeviceType.FALL_ALARM:
      return (
        <img src={FallSensorImage}/>
      );
    case DeviceType.DECT_MOUNTED:
      return (
        <img src={DectSensorImage}/>
      );
    case DeviceType.WRIST_BAND:
      return (
        <Tooltip
          popoverId="wristbandPopover"
          labelId={DeviceTypeLabel.WRIST_BAND_LABEL}
          defaultMessage="Armbånd"
        >
          <AlarmWristband size="large"/>
        </Tooltip>
      )
    case DeviceType.BASE_STATION:
      return (
        <Tooltip
          popoverId="baseStationPopover"
          labelId={DeviceTypeLabel.BASE_STATION_LABEL}
          defaultMessage="Basestasjon"
        >
          <Icons.ChargingStation mediumSecondary/>
        </Tooltip>
      )
    case '24':
    default:
      return (
        null
      );
  }
};
